import React, { useState, useEffect } from 'react';
import useRevenueStore from '../stores/useRevenueStore';
import DashboardLayout from '../components/DashboardLayout';
import { useToast } from '../components/ToastContainer';
import useAuthStore from '../stores/useAuthStore';
import { Link, useNavigate } from 'react-router-dom';

const InstructorRevenuePage = () => {
  const { user: authUser } = useAuthStore()
  const navigate = useNavigate();
  const { revenue, requestWithdrawal } = useRevenueStore();
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [isLoadingRequest, setLoadingRequest] = useState(false);
  const toast = useToast();

  const fetchRevenue = useRevenueStore.getState().fetchRevenue;


  useEffect(() => {
    fetchRevenue();
  }, []);

  const handleWithdrawal = async (e) => {
    e.preventDefault();
    if (!withdrawalAmount || parseFloat(withdrawalAmount) < 1000) {
      return toast.error('Minimum withdrawal is ₦1,000');
    }
    setLoadingRequest(true)
    try {
      await requestWithdrawal(parseFloat(withdrawalAmount));
      toast.success('Withdrawal request submitted!');
      setWithdrawalAmount('');
      fetchRevenue();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to request withdrawal');
    } finally {
      setLoadingRequest(false)
    }
  };

  return (
    <DashboardLayout userRole={authUser.role}>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8 dark:text-neutral-300">Revenue Dashboard</h1>

        {!authUser.isPaystackVerified && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg mb-6">
            <p className="text-yellow-700 dark:text-yellow-300">
              ⚠️ You must set up a payout account before requesting withdrawals.
            </p>
            <button
              onClick={() => navigate('/instructor/revenue/withdrawal/payout-settings')}
              className="mt-2 px-4 py-2 bg-yellow-500 hover:bg-primary-600 text-white rounded-lg"
            >
              Set Up Payout Account
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-neutral-800 p-6 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow">
            <h3 className="text-neutral-500 dark:text-neutral-400 text-sm">Total Revenue</h3>
            <p className="text-2xl font-bold dark:text-neutral-300">₦{revenue?.totalRevenue?.toLocaleString() || '0'}</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 p-6 rounded-lg shadow">
            <h3 className="text-neutral-500 dark:text-neutral-400 text-sm">Wallet Balance</h3>
            <p className="text-2xl font-bold dark:text-neutral-300">₦{revenue?.walletBalance?.toLocaleString() || '0'}</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 p-6 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow">
            <h3 className="text-neutral-500 dark:text-neutral-400 text-sm">Total Earnings</h3>
            <p className="text-2xl font-bold dark:text-neutral-300">₦{revenue?.totalEarnings?.toLocaleString() || '0'}</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 p-6 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow">
            <h3 className="text-neutral-500 dark:text-neutral-400 text-sm">Total Withdrawn</h3>
            <p className="text-2xl font-bold dark:text-neutral-300">₦{revenue?.totalWithdrawn?.toLocaleString() || '0'}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-neutral-800 p-6 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow mb-8">
          <div className='flex justify-between'>
            <h2 className="text-xl font-semibold mb-4 dark:text-neutral-300">Request Withdrawal</h2>
            {authUser.isPaystackVerified && (<h2 className="text-xs font-semibold mb-4 dark:text-primary-300"><Link to="/instructor/revenue/withdrawal/payout-settings">Change Account</Link></h2>)}
          </div>

          {authUser.isPaystackVerified && (
            <div className="text-sm p-4 bg-green-50 dark:bg-green-900/20 rounded-lg mb-6">
              <p className='text-neutral-700 font-semibold mb-2'>Transfer Earning to</p>
              <p className="text-green-700 dark:text-green-300">
                ✅ Account name: <strong>{authUser.bankAccount.accountName}</strong>
              </p>
              <p className="text-green-700 dark:text-green-300">
                ✅ Account Number: <strong>{authUser.bankAccount.accountNumber}</strong>
              </p>
            </div>
          )}

          <form onSubmit={handleWithdrawal} className="flex gap-4">
            <input
              type="number"
              value={withdrawalAmount}
              onChange={(e) => setWithdrawalAmount(e.target.value)}
              placeholder="Enter amount (min ₦1,000)"
              className="flex-1 px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
              min="1000"
            />
            <button
              type="submit"
              disabled={!authUser.isPaystackVerified || !withdrawalAmount || isLoadingRequest}
              className={`px-6 py-2 rounded-lg text-white transition-colors duration-200 ${!authUser.isPaystackVerified || !withdrawalAmount
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary-500 hover:bg-primary-600'
                }`}
            >
              {isLoadingRequest ? ('Requesting ....') : ('Request Withdrawal')}
            </button>
          </form>
        </div>

        <div className="bg-white dark:bg-neutral-800 dark:text-neutral-300 p-6 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4 dark:text-neutral-400">Course Revenue</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3">Course</th>
                  <th className="text-left py-3">Enrollments</th>
                  <th className="text-left py-3">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {revenue?.courses?.map(course => (
                  <tr key={course._id} className="border-b">
                    <td className="py-3">{course.title}</td>
                    <td className="py-3">{course.enrollments}</td>
                    <td className="py-3">₦{course.revenue.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default InstructorRevenuePage;